import '@styles/main.scss'
import 'react-datepicker/dist/react-datepicker.css'

import { initTracking } from '@utils/helpers/index-client'
import type { AppProps } from 'next/app'
import Head from 'next/head'
import { useEffect } from 'react'

import { ContextProvider } from '../contexts'
import { useBrand } from '../contexts/useBrand'

const privacyUrls: Record<string, string> = {
  toyota: 'https://www.toyota.co.uk/footer/privacy-policy',
  lexus: 'https://www.lexus.co.uk/customer-data/privacy-policy',
  kinto: 'https://www.kinto-mobility.co.uk/privacy-policy',
}

const brandNames: Record<string, string> = {
  toyota: 'Toyota',
  lexus: 'Lexus',
  kinto: 'Kinto',
}

const Footer = () => {
  const brand = useBrand()
  const currentYear = new Date().getFullYear()
  const brandName = brandNames[brand] || 'Toyota'
  const privacyUrl = privacyUrls[brand] || privacyUrls.toyota

  return (
    <footer className="mt-auto py-4 text-center text-sm text-gray-600">
      <p>
        © {brandName}. All Rights Reserved {currentYear}.{' '}
        <a
          href={privacyUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="underline hover:text-gray-900"
        >
          Privacy Notice
        </a>
      </p>
    </footer>
  )
}

interface Tracking {
  page: string
  form: string
  variant: string
}

interface AppPageProps {
  models?: Array<any>
  content?: Record<string, unknown>
  settings?: Record<string, unknown>
  title: string
  tracking: Tracking
  description: string
  isFinance?: boolean
}

const MyApp = ({ Component, pageProps }: AppProps<AppPageProps>) => {
  const metaTitleSuffix = pageProps?.isFinance
    ? pageProps?.settings?.finance_title_suffix
    : pageProps?.settings?.title_suffix
  const metaTitle = `${pageProps.title} | ${metaTitleSuffix}`
  const metaDesc = pageProps.description || ''

  useEffect(() => {
    initTracking(true)

    return () => {
      initTracking(false)
    }
  }, [])

  return (
    <ContextProvider>
      <>
        <Head>
          <meta
            key="viewport"
            name="viewport"
            content="width=device-width, initial-scale=1, viewport-fit=cover"
          />
          <meta key="robots" name="robots" content="noindex, nofollow" />
          <meta
            key="format-detection"
            name="format-detection"
            content="telephone=no"
          />
          <title key="title">{metaTitle}</title>
          <meta key="description" name="description" content={metaDesc} />
        </Head>
        <div className={`flex flex-col min-h-screen`}>
          <Component {...pageProps} />
          <Footer />
        </div>
      </>
    </ContextProvider>
  )
}

export default MyApp
